import {
    CalculatorPresentationContractError,
    requirePresentationArray,
    requirePresentationFields,
    requirePresentationFinite,
    requirePresentationNullableString,
    requirePresentationRecord,
    requirePresentationString,
} from "./calculator-presentation-contract.js";
import {
    calculatorDataEnumLabel as enumLabel,
    calculatorDataText as t,
} from "./calculator-data-i18n.js";
import {
    calculatorItemQuantityRangeDisplay,
    parseCalculatorItemQuantityV1,
} from "./calculator-item-quantity-presentation.js";

const LOOT_FACT_LABELS = Object.freeze({
    groupMembership: Object.freeze({
        key: "calculator.data.provenance_graph.group_membership",
        fallback: "Group membership · { $group }",
        usesGroup: true,
    }),
    groupPresence: Object.freeze({
        key: "calculator.data.provenance_graph.group_presence",
        fallback: "Group presence · { $group }",
        usesGroup: true,
    }),
    groupRate: Object.freeze({
        key: "calculator.data.provenance_graph.group_rate",
        fallback: "Group rate · { $group }",
        usesGroup: true,
    }),
    itemPresence: Object.freeze({
        key: "calculator.data.provenance_graph.item_presence",
        fallback: "Item presence",
        usesGroup: false,
    }),
    itemRate: Object.freeze({
        key: "calculator.data.provenance_graph.item_rate",
        fallback: "Item rate · { $group }",
        usesGroup: true,
    }),
    itemQuantity: Object.freeze({
        key: "calculator.data.provenance_graph.item_quantity",
        fallback: "Item quantity · { $group }",
        usesGroup: true,
    }),
    itemQuantityDefault: Object.freeze({
        key: "calculator.data.provenance_graph.item_quantity_default",
        fallback: "Default item quantity",
        usesGroup: false,
    }),
});
const RATE_BASES = new Set([
    "ratio",
    "percent",
    "denormalizedWeight",
    "rawWeight",
    "perCatch",
    "perHour",
]);
const UNAVAILABLE_REASON_CODES = new Set([
    "missingSourceFact",
    "unsupportedSourceVariant",
    "noEquivalentRawWeight",
    "incompleteProvenance",
    "notApplicable",
    "modelSelectionRequired",
]);
const MEMBERSHIP_VALUES = new Set(["member", "notMember", "unknown"]);
const PRESENCE_VALUES = new Set(["present", "absent", "unknown"]);
const CONDITION_SUBJECTS = new Set(["mastery", "lifeskillLevel", "fishingLevel"]);
const CONDITION_COMPARISONS = new Set([
    "equal",
    "notEqual",
    "greaterThan",
    "greaterThanOrEqual",
    "lessThan",
    "lessThanOrEqual",
]);
const COMPARISON_TEXT = Object.freeze({
    equal: "=",
    notEqual: "≠",
    greaterThan: ">",
    greaterThanOrEqual: "≥",
    lessThan: "<",
    lessThanOrEqual: "≤",
});

function requireEnum(value, values, path) {
    const parsed = requirePresentationString(value, path);
    if (!values.has(parsed)) {
        throw new CalculatorPresentationContractError(path, "unsupported value");
    }
    return parsed;
}

function literalNumber(value, path) {
    return String(requirePresentationFinite(value, path, { minimum: 0 }));
}

export function calculatorLootProvenanceFactLabel(
    factKind,
    { groupLabel = null, path = "lootProvenance" } = {},
) {
    const kind = requirePresentationString(factKind, `${path}.factKind`);
    const presentation = LOOT_FACT_LABELS[kind];
    if (!presentation) {
        throw new CalculatorPresentationContractError(
            `${path}.factKind`,
            "unsupported loot provenance fact kind",
        );
    }
    const group = presentation.usesGroup
        ? requirePresentationString(groupLabel, `${path}.groupLabel`)
        : null;
    return t(presentation.key, presentation.fallback, { group });
}

export function calculatorLootProvenanceUnavailableValueText(
    value,
    path = "lootProvenance.value",
) {
    const unavailable = requirePresentationFields(value, ["reason", "state"], path);
    if (unavailable.state !== "unavailable") {
        throw new CalculatorPresentationContractError(`${path}.state`, "expected unavailable");
    }
    const reasonPath = `${path}.reason`;
    const reason = requirePresentationFields(
        unavailable.reason,
        ["code", "sourceId", "detailCode"],
        reasonPath,
    );
    const code = requireEnum(reason.code, UNAVAILABLE_REASON_CODES, `${reasonPath}.code`);
    const sourceId = requirePresentationNullableString(
        reason.sourceId,
        `${reasonPath}.sourceId`,
    );
    const detailCode = requirePresentationNullableString(
        reason.detailCode,
        `${reasonPath}.detailCode`,
    );
    return [
        enumLabel("unavailable"),
        enumLabel(code),
        sourceId,
        detailCode,
    ].filter((part) => part !== null).join(" · ");
}

export function calculatorLootProvenanceRateValueText(
    value,
    path = "lootProvenance.rate",
) {
    const rate = requirePresentationRecord(value, path);
    if (rate.state === "unknown") {
        requirePresentationFields(rate, ["state"], path);
        return enumLabel("unknown");
    }
    if (rate.state === "unavailable") {
        return calculatorLootProvenanceUnavailableValueText(rate, path);
    }
    if (rate.state !== "known") {
        throw new CalculatorPresentationContractError(
            `${path}.state`,
            "unsupported rate state",
        );
    }
    const known = requirePresentationFields(rate, ["basis", "state", "value"], path);
    const basis = requireEnum(known.basis, RATE_BASES, `${path}.basis`);
    return `${literalNumber(known.value, `${path}.value`)} · ${enumLabel(basis)}`;
}

export function calculatorLootProvenanceQuantityValueText(
    value,
    { locale = "en-US", path = "lootProvenance.quantity" } = {},
) {
    const quantity = parseCalculatorItemQuantityV1(value, path);
    if (quantity.state === "unknown") {
        return enumLabel("unknown");
    }
    if (quantity.state === "unavailable") {
        return calculatorLootProvenanceUnavailableValueText(quantity, path);
    }
    return calculatorItemQuantityRangeDisplay(quantity, { locale, path }).text;
}

export function calculatorLootProvenanceAuthoredValueText(
    value,
    { locale = "en-US", path = "lootProvenance.value" } = {},
) {
    const authored = requirePresentationFields(value, ["kind", "value"], path);
    const kind = requirePresentationString(authored.kind, `${path}.kind`);
    if (kind === "membership") {
        return enumLabel(requireEnum(
            authored.value,
            MEMBERSHIP_VALUES,
            `${path}.value`,
        ));
    }
    if (kind === "presence") {
        return enumLabel(requireEnum(
            authored.value,
            PRESENCE_VALUES,
            `${path}.value`,
        ));
    }
    if (kind === "rate") {
        return calculatorLootProvenanceRateValueText(authored.value, `${path}.value`);
    }
    if (kind === "quantity") {
        return calculatorLootProvenanceQuantityValueText(authored.value, {
            locale,
            path: `${path}.value`,
        });
    }
    throw new CalculatorPresentationContractError(
        `${path}.kind`,
        "unsupported loot provenance value kind",
    );
}

export function calculatorLootProvenanceScalarConditionsText(
    value,
    path = "lootProvenance.conditions",
) {
    const conditions = requirePresentationArray(value, path);
    if (conditions.length === 0) {
        return t("calculator.data.none", "None");
    }
    return conditions.map((condition, index) => {
        const conditionPath = `${path}[${index}]`;
        const record = requirePresentationRecord(condition, conditionPath);
        const kind = requirePresentationString(record.kind, `${conditionPath}.kind`);
        if (kind !== "numeric") {
            throw new CalculatorPresentationContractError(
                `${conditionPath}.kind`,
                "unsupported condition kind",
            );
        }
        const numeric = requirePresentationFields(
            record,
            ["kind", "subject", "comparison", "value"],
            conditionPath,
        );
        const subject = requireEnum(
            numeric.subject,
            CONDITION_SUBJECTS,
            `${conditionPath}.subject`,
        );
        const comparison = requireEnum(
            numeric.comparison,
            CONDITION_COMPARISONS,
            `${conditionPath}.comparison`,
        );
        return `${enumLabel(subject)} ${COMPARISON_TEXT[comparison]} ${literalNumber(
            numeric.value,
            `${conditionPath}.value`,
        )}`;
    }).join(" · ");
}

export function calculatorLootProvenanceConditionSetText(
    value,
    path = "lootProvenance.conditions",
) {
    const conditionSet = requirePresentationRecord(value, path);
    if (conditionSet.state === "unconditional") {
        requirePresentationFields(conditionSet, ["state"], path);
        return null;
    }
    if (conditionSet.state === "known") {
        const known = requirePresentationFields(conditionSet, ["state", "values"], path);
        const values = requirePresentationArray(known.values, `${path}.values`);
        if (values.length === 0) {
            throw new CalculatorPresentationContractError(
                `${path}.values`,
                "known condition state requires at least one value",
            );
        }
        return calculatorLootProvenanceScalarConditionsText(values, `${path}.values`);
    }
    if (conditionSet.state === "sourceExpression") {
        const sourceExpression = requirePresentationFields(
            conditionSet,
            ["sourceExpression", "state"],
            path,
        );
        if (
            typeof sourceExpression.sourceExpression !== "string"
            || !sourceExpression.sourceExpression.trim()
        ) {
            throw new CalculatorPresentationContractError(
                `${path}.sourceExpression`,
                "expected a non-blank source expression",
            );
        }
        return sourceExpression.sourceExpression;
    }
    throw new CalculatorPresentationContractError(
        `${path}.state`,
        "unsupported condition state",
    );
}
